import { TitleButtonDivider } from '@/common/buttons/TitleButtonDivider';
import { EntityPerspective } from '@/common/entityFramework/entityPerspective/EntityPerspective';
import { useFilterButtons } from '@/common/entityFramework/entityPerspective/hooks/useFilterButtons';
import { RowIcon } from '@/common/entityFramework/entityPerspective/support/RowIcon';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useRef } from 'react';

export const GilMnSapComplReconPerspective = () => {

  const entityPerspectiveRef = useRef();

  const views = [
    {
      name: 'SAP Reconiliction',
      dims: ['PERIOD', 'CHANNEL', 'PROD'],
      kpis: ['SAP_AMT', 'SAP_QTY', 'MN_AMT', 'MN_QTY', 'DELTA_AMT', 'DELTA_QTY'],
      numFrozenDims: 1,
      dimToPeriodicities: {
        PERIOD: ['YEAR', 'QTR', 'MON']
      }
    }
  ]

  const { filterButtonStages, filterButtons, onViewReset } = useFilterButtons([
    {
      key: 'discrepancy',
      icon: fas.faExclamationTriangle,
      colors: [null, 'red', 'green'],
      toolTips: ['Discrepancies and No Discrepancies', 'Discrepancies', 'No Discrepancies'],
    }, {
      key: 'accepted',
      icon: fas.faPencil,
      colors: [null, 'green', 'grey'],
      toolTips: ['Accepted and None Accepted', 'Accepted', 'None Accepted']
    }
  ])

  const augButtons = (buttons) => {
    buttons.unshift(...filterButtons, <TitleButtonDivider key="filterButtonDivider" />);
  }

  const augReqParam = useCallback((reqParam) => {
    // Discrepancy
    const discrepancyButtonState = filterButtonStages.discrepancy;
    if (discrepancyButtonState > 0) {
      reqParam.expr = ExprUtils.and(
        reqParam.expr,
        discrepancyButtonState === 1 ? ExprUtils.whereParams('discrepancy', 1) : ExprUtils.isNot('discrepancy', 1)
      )
    }

    // Accepted
    const acceptedButtonState = filterButtonStages.accepted;
    if (acceptedButtonState > 0) {
      reqParam.expr = ExprUtils.and(
        reqParam.expr,
        acceptedButtonState === 1 ? ExprUtils.whereParams('accepted', 1) : ExprUtils.isNot('accepted', 1)
      )
    }
  }, [filterButtonStages])

  const rowIconsFn = (data) => {
    let rowIcons = [];

    // Closed
    if (data.closed) {
      rowIcons.push(<RowIcon key="closed" icon={fas.faCheckCircle} color="green" toolTip="Period is closed" />);
    }

    // Discrepancy row icons
    if (data.discrepancy === 1) {
      rowIcons.push(<RowIcon key="discrepancy" icon={fas.faExclamationTriangle} color="red" toolTip="Discrepancy" />);
    } else if (data.discrepancy > 0) {
      rowIcons.push(<RowIcon key="containsDiscrepancy" icon={fas.faExclamationTriangle} color="orange" toolTip="Contains a discrepancy" />);
    }

    // Accepted row icons
    if (data.accepted === 1) {
      rowIcons.push(<RowIcon key="accepted" icon={fas.faPencil} color="90ee90" toolTip="Accepted" />);
    } else if (data.accepted > 0) {
      // Light green
      rowIcons.push(<RowIcon key="containsAccepted" icon={fas.faPencil} color="green" toolTip="Partly accepted" />);
    }

    return rowIcons;
  }

  const isPeriodClosed = (node) => {
    while (node.data.dim !== 'PERIOD') {
      node = node.parent;
    }
    return node.data.closed;
  }


  const getContextMenuItems = (params) => {
    let contextMenus = entityPerspectiveRef.current.getContextMenuItems(params);

    const navigationItems = [
      {
        name: 'Navigate to SAP Transactions',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'SAP_AMT', 'SAP Transactions')
      },
      {
        name: 'Navigate to ModelN Transactions',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'MN_AMT', 'ModelN Transactions')
      },
      {
        name: 'SAP Transactions and ModelN Transactions Lookup',
        action: () => entityPerspectiveRef.current.navigateToLookupViewer(params, 'SAP_AMT', 'SAP Transactions', 'MN_AMT', 'ModelN Transactions')
      },
    ];

     contextMenus.push(
      {
        name: 'Close this Period',
        disabled: isPeriodClosed(params.node),
        action: () => entityPerspectiveRef.current.openActionDialog(params, 'Close this Period', 'MN_SAP_COMPL_CLOSE_ACTION_PLUGIN'),
        icon: fas.faCheckCircle                                                         
      },
      { 
        name: 'Transactions', subMenu: navigationItems 
      },
      {
        name: 'Accept Discrepancy',
        disabled: isPeriodClosed(params.node),
        action: () => entityPerspectiveRef.current.openActionDialog(params, 'Accept Discrepancy', 'COMPL_DISCREPANCY_ACTION_PLUGIN', ),
        icon: '<span class="fas fa-check"></span>'
      }, 
      {
        name: 'Export',
        action: () => entityPerspectiveRef.current.openExportDialog(params, views.name),
        icon: fas.faArrowCircleDown
      }
    );

    return contextMenus;
  }

  return (
    <EntityPerspective
      ref={entityPerspectiveRef}
      views={views}
      rowIconsFn={rowIconsFn}
      augButtons={augButtons}
      augReqParam={augReqParam}
      onViewReset={onViewReset}
      gridOptions={{
        getContextMenuItems
      }}
    />
  )

}